import ExcavationPermitDetails from "./ExcavationPermitDetails";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { Box, Divider, Typography, CircularProgress } from "@mui/material";
import API_URL from "../../src/Config";
import { IoArrowBackCircle } from "react-icons/io5";

export default function ExcavationViewInspection({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { permit_id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [savedPermit, setSavedPermit] = useState(null);
  const [departmentError, setDepartmentError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [request_status, setRequest_status] = useState("");

  // Popup states
  const [isApprovePopupOpen, setIsApprovePopupOpen] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [reason, setReason] = useState("");

  const showSnackbar = (message, type = "success") => {
    setSnackbarMessage(message);
    setSeverity(type);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // -------- APPROVE ----------
  const handleSaveClick = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      if (!authKey) return;

      const response = await axios.put(
        `${API_URL}/api/v2/fire_safety/approve`,
        { permit_id: permit_id, request_type: "new" },
        { headers: { Authorization: authKey } }
      );

      if (response.status === 200) {
        setSeverity("success");
        showSnackbar("Work permit approved successfully!");
        setTimeout(() => navigate("/work-permit-inspection"), 1000);
      }
    } catch (error) {
      console.error("Error approving permit:", error.message);
      showSnackbar("Failed to approve permit", "error");
    }
  };

  const handleApproveConfirm = () => setIsApprovePopupOpen(true);
  const closeApprovePopup = () => setIsApprovePopupOpen(false);
  const confirmApprove = () => {
    handleSaveClick();
    setIsApprovePopupOpen(false);
  };

  // -------- REJECT ----------
  const handleRejectClick = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);
  const handleReasonChange = (e) => setReason(e.target.value);

  const handleRejectConfirm = () => {
    handleRejectClick();
    setShowRejectPopup(false);
  };

  const handleSaveRejectClick = async () => {
    const payload = {
      permit_id: permit_id,
      request_type: "new",
      reason: reason,
    };

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      if (!authKey) throw new Error("Authorization key not found");

      const response = await axios.post(
        `${API_URL}/api/reject_permit`,
        payload,
        { headers: { Authorization: authKey } }
      );

      if (response.status === 200) {
        setSeverity("success");
        showSnackbar("Permit rejected successfully.");
        setTimeout(() => navigate("/work-permit-inspection"), 1000);
      } else {
        throw new Error(response.data.message || "Failed to reject permit.");
      }
    } catch (error) {
      console.error("Error rejecting permit:", error);
      setSeverity("error");
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while rejecting the permit."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWorkPermitDetails = async () => {
    try {
      setError(null);
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/get_permit_work_details/${permit_id}`,
        { headers: { Authorization: authKey } }
      );
      const permit = response.data?.permit_details?.[0];
      if (!permit) throw new Error("Permit not found");
      setSavedPermit(permit);
      setRequest_status(permit.request_status);
    } catch {
      setError("Failed to load work permit details");
    }
  };

  const fetchDepartmentApprovals = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/permit/department/approvals/${permit_id}`,
        { headers: { Authorization: authKey } }
      );
      setDepartments(response.data.department_approvals || []);
      setDepartmentError("");
    } catch (error) {
      setDepartmentError("The latest department approvals could not be loaded.");
      console.error("Error fetching department approvals:", error);
      showSnackbar("Failed to fetch department details", "error");
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/permit/department/attachments/download/${attachmentId}`,
        { headers: { Authorization: authKey }, responseType: "blob" }
      );

      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      showSnackbar("Attachment downloaded successfully");
    } catch (error) {
      console.error("Error downloading attachment:", error);
      showSnackbar("Error downloading attachment", "error");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchWorkPermitDetails(), fetchDepartmentApprovals()]);
      setIsLoading(false);
    };
    if (permit_id) loadData();
  }, [permit_id]);

  const handleBack = () => navigate("/work-permit-inspection");

  if (isLoading) {
    return (
      <div
        className={`data-list-content-container page-content ${
          (isSidebarExpanded ? "expanded" : "collapsed") + " excavation-details-page"
        }`}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`data-list-content-container page-content ${
          (isSidebarExpanded ? "expanded" : "collapsed") + " excavation-details-page"
        }`}
      >
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </div>
    );
  }

  return (
    <div
      className={`data-list-content-container page-content ${
        (isSidebarExpanded ? "expanded" : "collapsed") + " excavation-details-page"
      }`}
    >
      <div className="data-form-content">
        {/* Header */}
        <div className="data-form-header">
          <div className="data-form-header-content">
            <div className="data-form-heading-content-left-side">
              <IoArrowBackCircle
                style={{ color: "#233b80", paddingTop: "3px" }}
                size={25}
                onClick={handleBack}
              />
              <span className="data-form-heading-text">
                Excavation Permit - View Details
              </span>
            </div>
          </div>
        </div>
        <Divider />

        <div className="data-form-body-permit-fands">
          <ExcavationPermitDetails
            work={savedPermit || {}}
            departments={departments}
            warning={departmentError}
            onDownloadAttachment={handleDownloadAttachment}
          />
        </div>

        {/* Footer */}
        <div className="footer">
          <div className="button-group">
            <button className="data-form-cancel-button" onClick={handleBack}>
              Cancel
            </button>

            {request_status === "Pending Approval" && (
              <>
                <button
                  style={{ marginLeft: "10px" }}
                  className="data-form-save-button approverd-button-permit approve_btn_clr"
                  type="button"
                  onClick={handleApproveConfirm}
                >
                  Approve
                </button>

                <button
                  className="data-form-reject-button"
                  type="button"
                  onClick={() => setShowRejectPopup(true)}
                >
                  Reject
                </button>
              </>
            )}
          </div>

          {/* APPROVE CONFIRM POPUP */}
          {isApprovePopupOpen && (
            <div className="permit-approval-popup">
              <div className="permit-approval-popup-content">
                <div className="approval-popup-header">Confirm Approval</div>
                <p className="approval-popup-message">
                  Are you sure you want to approve this permit?
                </p>
                <div className="approval-popup-buttons">
                  <button className="approve-button approve_btn_clr" onClick={confirmApprove}>
                    Yes, Approve
                  </button>
                  <button className="cancel-button" onClick={closeApprovePopup}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* REJECT CONFIRM POPUP */}
          {showRejectPopup && (
            <div className="confirm-delete-popup">
              <div className="confirm-delete-popup-content">
                <div className="confirm-model">
                  <div className="confirm-model-content">
                    <div className="confirm-model-content-body">
                      <p>Are you sure you want to reject this request?</p>
                    </div>
                    <div className="confirm-model-content-footer">
                      <button className="reject-confirm-button" onClick={handleRejectConfirm}>
                        Yes, Reject
                      </button>
                      <button className="reject-cancel-button" onClick={() => setShowRejectPopup(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* REJECT REASON POPUP */}
          {isPopupOpen && (
            <div className="permit-popup-model">
              <div className="form-permit-popup-content">
                <div className="reminder-popup-model-header">
                  <div className="reminder-popup-model-content-header">
                    <div className="reminder-popup-model-content-header-left-part">
                      Reject Permit
                    </div>
                    <div className="reminder-popup-model-content-header-right-part"></div>
                  </div>

                  <div className="permit-popup-new-form">
                    <form className="permit-popup-form-detail">
                      <div className="new-data-form">
                        <div className="new-form-group email-filed">
                          <label htmlFor="reason">
                            Reason for rejection
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <textarea
                              id="reason"
                              name="reason"
                              placeholder="Enter reason"
                              value={reason}
                              onChange={handleReasonChange}
                              rows="4"
                            />
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="footers">
                    <div className="form-button-group">
                      <button
                        className="form-reject-button"
                        onClick={handleSaveRejectClick}
                        disabled={isLoading}
                      >
                        {isLoading ? "Rejecting..." : "Reject"}
                      </button>
                      <button className="person-user-cancel-button" onClick={closePopup}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={severity} variant="filled">
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </div>
    </div>
  );
}